import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Receipt, Download, User } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const ClientDetail = () => {
  const { clientEmail } = useParams();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    fetchClientData();
  }, [clientEmail]);

  const fetchClientData = async () => {
    try {
      const response = await axios.get(`${API}/clients/${encodeURIComponent(clientEmail)}`);
      setQuotes(response.data.quotes || []);
      setInvoices(response.data.invoices || []);
      if (response.data.quotes && response.data.quotes.length > 0) {
        setClientName(response.data.quotes[0].client_name);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQuotePDF = async (quoteId, quoteNumber) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${quoteNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/clients')} className="w-10 h-10 p-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <User className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{clientName || clientEmail}</h1>
            <p className="text-sm text-slate-500">{clientEmail}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-2xl font-bold">{quotes.length}</p>
              <p className="text-sm text-slate-500">Devis</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-2xl font-bold">{invoices.length}</p>
              <p className="text-sm text-slate-500">Factures</p>
            </div>
          </div>
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">Devis</h2>
        <div className="space-y-2">
          {quotes.map((quote) => (
            <Card key={quote.quote_id} className="p-4 bg-white border border-slate-200 hover:shadow-md cursor-pointer" onClick={() => navigate(`/quotes/${quote.quote_id}`)}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{quote.quote_number}</p>
                  <p className="text-sm text-slate-500">{new Date(quote.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-mono font-bold">{quote.total_ttc.toFixed(2)} €</p>
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDownloadQuotePDF(quote.quote_id, quote.quote_number); }}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {quotes.length === 0 && <p className="text-slate-500 text-center py-4">Aucun devis</p>}
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold mb-4">Factures</h2>
        <div className="space-y-2">
          {invoices.map((invoice) => (
            <Card key={invoice.invoice_id} className="p-4 bg-white border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{invoice.invoice_number}</p>
                  <p className="text-sm text-slate-500">{new Date(invoice.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-mono font-bold">{invoice.amount_ttc.toFixed(2)} €</p>
                  <Button size="sm" variant="ghost" onClick={() => handleDownloadInvoicePDF(invoice.invoice_id, invoice.invoice_number)}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {invoices.length === 0 && <p className="text-slate-500 text-center py-4">Aucune facture</p>}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
